/*
  ANAVRIN · PDP v3 ("gallery") — behaviour for the challenger template.

  Three custom elements, each one self-contained so a section can be
  added, removed or re-rendered by the theme editor without leaking
  listeners:

    <v3-story>  sticky media + scroll-linked steps
    <v3-scale>  drag-to-wipe scale comparator
    <v3-rail>   sticky add-to-cart rail

  Every element degrades to readable static markup when its API is
  missing, so this file is loaded `defer` and is never render blocking.
*/
(function () {
  'use strict';

  var supportsIO = 'IntersectionObserver' in window;
  var reduceMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ── Story scroll ─────────────────────────────────────────────── */

  var V3Story = (function () {
    function V3Story() {
      return Reflect.construct(HTMLElement, [], V3Story);
    }

    V3Story.prototype = Object.create(HTMLElement.prototype);
    V3Story.prototype.constructor = V3Story;
    Object.setPrototypeOf(V3Story, HTMLElement);

    V3Story.prototype.connectedCallback = function () {
      this.steps = Array.prototype.slice.call(this.querySelectorAll('[data-v3-step]'));
      this.frames = Array.prototype.slice.call(this.querySelectorAll('[data-v3-frame]'));

      this.bindTabs();

      // No observer, or a single step: keep the stacked static layout.
      if (!supportsIO || this.steps.length < 2) {
        this.classList.add('v3-story--static');
        return;
      }

      this.setActive(0);

      var self = this;
      this.observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            var index = self.steps.indexOf(entry.target);
            if (index > -1) self.setActive(index);
          });
        },
        // Band across the middle of the viewport: a step takes over
        // once it reaches the vertical centre, which lines the copy up
        // with the sticky image rather than the top of the screen.
        { rootMargin: '-45% 0px -45% 0px', threshold: 0 }
      );

      this.steps.forEach(function (step) {
        self.observer.observe(step);
      });
    };

    // Mobile tabs. The row is a scroll container, so a tap scrolls it to the
    // step rather than swapping panels - swipe and tap stay in agreement.
    V3Story.prototype.bindTabs = function () {
      this.tabs = Array.prototype.slice.call(this.querySelectorAll('[data-v3-story-tab]'));
      if (!this.tabs.length) return;

      var self = this;
      this.row = this.querySelector('.v3-story__steps');

      this.onTab = function (event) {
        var tab = event.target.closest('[data-v3-story-tab]');
        if (!tab) return;
        var index = parseInt(tab.getAttribute('data-v3-story-tab'), 10);
        var step = self.steps[index];
        if (!step) return;

        if (self.row && self.row.scrollWidth > self.row.clientWidth) {
          self.row.scrollTo({ left: step.offsetLeft - self.row.offsetLeft, behavior: 'smooth' });
        } else {
          step.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        self.markTab(index);
      };
      this.addEventListener('click', this.onTab);

      // Keep the tabs honest when the reader swipes instead of tapping.
      if (this.row && supportsIO) {
        this.rowObserver = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (!entry.isIntersecting) return;
              self.markTab(self.steps.indexOf(entry.target));
            });
          },
          { root: this.row, threshold: 0.6 }
        );
        this.steps.forEach(function (step) {
          self.rowObserver.observe(step);
        });
      }
    };

    V3Story.prototype.markTab = function (index) {
      if (index < 0 || !this.tabs) return;
      this.tabs.forEach(function (tab, i) {
        tab.setAttribute('aria-selected', i === index ? 'true' : 'false');
      });
    };

    V3Story.prototype.disconnectedCallback = function () {
      if (this.observer) this.observer.disconnect();
      if (this.rowObserver) this.rowObserver.disconnect();
      if (this.onTab) this.removeEventListener('click', this.onTab);
    };

    V3Story.prototype.setActive = function (index) {
      if (this.activeIndex === index) return;
      this.activeIndex = index;

      this.steps.forEach(function (step, i) {
        step.classList.toggle('is-active', i === index);
      });
      this.frames.forEach(function (frame, i) {
        frame.classList.toggle('is-active', i === index);
      });
    };

    return V3Story;
  })();

  /* ── Scale comparator ─────────────────────────────────────────── */

  var V3Scale = (function () {
    function V3Scale() {
      return Reflect.construct(HTMLElement, [], V3Scale);
    }

    V3Scale.prototype = Object.create(HTMLElement.prototype);
    V3Scale.prototype.constructor = V3Scale;
    Object.setPrototypeOf(V3Scale, HTMLElement);

    V3Scale.prototype.connectedCallback = function () {
      this.stage = this.querySelector('[data-v3-scale-stage]');
      this.range = this.querySelector('[data-v3-scale-range]');
      if (!this.stage || !this.range) return;

      var self = this;
      this.onInput = function () {
        self.setPosition(self.range.value);
      };
      this.range.addEventListener('input', this.onInput);
      this.setPosition(this.range.value);
    };

    V3Scale.prototype.disconnectedCallback = function () {
      if (this.range && this.onInput) this.range.removeEventListener('input', this.onInput);
    };

    V3Scale.prototype.setPosition = function (value) {
      var pct = Math.min(100, Math.max(0, parseFloat(value) || 0));
      this.stage.style.setProperty('--v3-scale-pos', pct + '%');
      this.range.setAttribute('aria-valuenow', String(Math.round(pct)));
    };

    return V3Scale;
  })();

  /* ── Sticky add-to-cart rail ──────────────────────────────────── */

  var V3Rail = (function () {
    function V3Rail() {
      return Reflect.construct(HTMLElement, [], V3Rail);
    }

    V3Rail.prototype = Object.create(HTMLElement.prototype);
    V3Rail.prototype.constructor = V3Rail;
    Object.setPrototypeOf(V3Rail, HTMLElement);

    V3Rail.prototype.connectedCallback = function () {
      this.bar = this.querySelector('[data-v3-rail-bar]');
      if (!this.bar) return;

      // Watch the real buy button. While any part of it is on screen the
      // rail stays hidden, so the two CTAs never compete.
      var selector = this.getAttribute('data-watch') || '.product-form__buttons';
      this.target = document.querySelector(selector);

      this.mirrorPrice();

      if (!supportsIO || !this.target) {
        // Without an anchor to watch, showing the rail permanently is
        // still better than never showing it at all.
        this.bar.classList.add('is-visible');
        return;
      }

      var self = this;
      this.observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            self.bar.classList.toggle('is-visible', !entry.isIntersecting);
          });
        },
        { rootMargin: '0px 0px -20% 0px', threshold: 0 }
      );
      this.observer.observe(this.target);
    };

    // Keep the rail price in step with the buy box. This section does not
    // re-render on a variant change, so the value is copied from the live
    // price node instead of being recomputed here.
    V3Rail.prototype.mirrorPrice = function () {
      var slot = this.querySelector('[data-v3-rail-price]');
      var source = document.querySelector('product-info .price__regular .price-item, product-info .price');
      if (!slot || !source || !('MutationObserver' in window)) return;

      var copy = function () {
        var text = source.textContent.trim().split(/\s{2,}|\n/)[0];
        if (text && text !== slot.textContent.trim()) slot.textContent = text;
      };

      copy();
      this.priceObserver = new MutationObserver(copy);
      this.priceObserver.observe(source, { childList: true, characterData: true, subtree: true });
    };

    V3Rail.prototype.disconnectedCallback = function () {
      if (this.observer) this.observer.disconnect();
      if (this.priceObserver) this.priceObserver.disconnect();
    };

    // The rail's button just forwards the click to the real product form,
    // so variant state, Kaching bundles and the cart drawer all keep working.
    V3Rail.prototype.submit = function (event) {
      var form = document.querySelector(this.getAttribute('data-form') || 'form.product-form');
      if (!form) return;
      event.preventDefault();
      var button = form.querySelector('[type="submit"][name="add"], [type="submit"]');
      if (button) {
        button.click();
      } else if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
      } else {
        form.submit();
      }
    };

    return V3Rail;
  })();

  /* ── Lights on / off ──────────────────────────────────────────── */

  var V3Lights = (function () {
    function V3Lights() {
      return Reflect.construct(HTMLElement, [], V3Lights);
    }

    V3Lights.prototype = Object.create(HTMLElement.prototype);
    V3Lights.prototype.constructor = V3Lights;
    Object.setPrototypeOf(V3Lights, HTMLElement);

    V3Lights.prototype.connectedCallback = function () {
      this.layers = Array.prototype.slice.call(this.querySelectorAll('[data-v3-lights-layer]'));
      this.options = Array.prototype.slice.call(this.querySelectorAll('[data-v3-lights-set]'));
      if (!this.layers.length || !this.options.length) return;

      var self = this;
      this.onClick = function (event) {
        var button = event.target.closest('[data-v3-lights-set]');
        if (button) self.set(button.getAttribute('data-v3-lights-set'));
      };
      this.addEventListener('click', this.onClick);

      this.set(this.getAttribute('data-start') || 'on');
    };

    V3Lights.prototype.disconnectedCallback = function () {
      if (this.onClick) this.removeEventListener('click', this.onClick);
    };

    V3Lights.prototype.set = function (state) {
      this.layers.forEach(function (layer) {
        layer.classList.toggle('is-on', layer.getAttribute('data-v3-lights-layer') === state);
      });
      this.options.forEach(function (option) {
        option.setAttribute(
          'aria-pressed',
          option.getAttribute('data-v3-lights-set') === state ? 'true' : 'false'
        );
      });
    };

    return V3Lights;
  })();

  /* ── Add a second kit ─────────────────────────────────────────── */

  var V3SecondKit = (function () {
    function V3SecondKit() {
      return Reflect.construct(HTMLElement, [], V3SecondKit);
    }

    V3SecondKit.prototype = Object.create(HTMLElement.prototype);
    V3SecondKit.prototype.constructor = V3SecondKit;
    Object.setPrototypeOf(V3SecondKit, HTMLElement);

    V3SecondKit.prototype.connectedCallback = function () {
      this.toggle = this.querySelector('[data-v3-sk-toggle]');
      this.qty = this.querySelector('[data-v3-sk-qty]');
      if (!this.toggle || !this.qty) return;

      var self = this;
      this.onChange = function () {
        // The hidden input is what the form posts; the checkbox is only the
        // control surface, so the two are kept in step here rather than by
        // naming the checkbox itself `quantity`.
        self.qty.value = self.toggle.checked ? '2' : '1';
      };
      this.toggle.addEventListener('change', this.onChange);
      this.onChange();
    };

    V3SecondKit.prototype.disconnectedCallback = function () {
      if (this.toggle && this.onChange) this.toggle.removeEventListener('change', this.onChange);
    };

    return V3SecondKit;
  })();

  /* ── Registration ─────────────────────────────────────────────── */

  function define(name, ctor) {
    if (!window.customElements || window.customElements.get(name)) return;
    window.customElements.define(name, ctor);
  }

  define('v3-story', V3Story);
  define('v3-scale', V3Scale);
  define('v3-rail', V3Rail);
  define('v3-lights', V3Lights);
  define('v3-second-kit', V3SecondKit);

  // Rail buttons are delegated so a section re-render in the theme
  // editor does not need a re-bind.
  document.addEventListener('click', function (event) {
    var trigger = event.target.closest && event.target.closest('[data-v3-rail-submit]');
    if (!trigger) return;
    var rail = trigger.closest('v3-rail');
    if (rail && typeof rail.submit === 'function') rail.submit(event);
  });

  if (reduceMotion) document.documentElement.classList.add('v3-reduce-motion');
})();
